import { useParams, Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Breadcrumb from '../components/Breadcrumb';
import SEO from '../components/SEO';
import { POLICIES } from '../data/policies';

const ORDER = ['shipping', 'returns', 'privacy', 'terms', 'cookies'];

export default function Policy() {
  const { slug } = useParams();
  const doc = POLICIES[slug];
  if (!doc) return <Navigate to="/" replace />;

  const others = ORDER.filter((s) => s !== slug);

  return (
    <div data-testid={`page-policy-${slug}`} className="container py-16 md:py-24">
      <SEO
        title={doc.title}
        description={`${doc.title} — ${doc.eyebrow}. ${doc.updated}.`}
        path={`/policies/${slug}`}
        breadcrumb={[
          { to: '/', label: 'Home' },
          { to: '/policies/privacy', label: 'Policies' },
          { label: doc.title },
        ]}
      />
      <Breadcrumb items={[
        { to: '/', label: 'Home' },
        { to: '/policies/privacy', label: 'Policies' },
        { label: doc.title },
      ]} className="mb-8" />

      <div className="grid gap-16 md:grid-cols-[240px_1fr]">
        <aside className="md:sticky md:top-32 md:self-start">
          <p className="overline text-muted-foreground mb-4">All Policies</p>
          <ul className="space-y-3">
            {ORDER.map((s) => (
              <li key={s}>
                <Link
                  to={`/policies/${s}`}
                  data-testid={`policy-nav-${s}`}
                  className={`text-sm link-underline ${s === slug ? 'opacity-100 font-medium' : 'opacity-70 hover:opacity-100'}`}
                >
                  {POLICIES[s].title}
                </Link>
              </li>
            ))}
          </ul>
        </aside>

        <article className="max-w-3xl">
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.85, 0, 0.15, 1] }}
          >
            <p className="overline text-muted-foreground">{doc.eyebrow}</p>
            <h1 className="mt-3 font-display text-5xl md:text-7xl leading-[0.95]">{doc.title}</h1>
            <p className="mt-4 overline text-muted-foreground">{doc.updated}</p>
          </motion.header>
          <div className="brushline my-12" />
          <div className="space-y-10">
            {doc.sections.map((s, i) => (
              <motion.section
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
              >
                <h2 className="font-display text-2xl md:text-3xl">{s.h}</h2>
                <p className="mt-3 text-muted-foreground text-lg leading-relaxed">{s.p}</p>
              </motion.section>
            ))}
          </div>

          <div className="brushline my-16" />
          <div className="flex flex-wrap gap-x-8 gap-y-3 overline text-muted-foreground">
            <span>Continue reading:</span>
            {others.map((s) => (
              <Link key={s} to={`/policies/${s}`} className="link-underline">{POLICIES[s].title}</Link>
            ))}
          </div>
        </article>
      </div>
    </div>
  );
}
